import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate, Link, useLocation } from "react-router-dom";
import { ArrowLeft, Clock, Paperclip } from "lucide-react";
import api, { formatDkk } from "@/lib/api";
import ProposalForm from "@/components/ProposalForm";
import ProblemClarification from "@/components/ProblemClarification";
import PrivateAttachment from "@/components/PrivateAttachment";
import ProfileAvatar from "@/components/profile/ProfileAvatar";
import AgreementScheduling from "@/components/AgreementScheduling";
import VideoConsultationEntry from "@/components/video/VideoConsultationEntry";
import { PageSkeleton } from "@/components/LoadingSkeleton";
import CancellationAndSupport from "@/components/CancellationAndSupport";

const durationLabel = (minutes) => {
  const value = Number(minutes) || 0;
  if (value < 60) return `${value} min.`;
  const hours = value / 60;
  const quantity = Number.isInteger(hours) ? String(hours) : String(hours).replace(".", ",");
  return `${quantity} ${hours === 1 ? "time" : "timer"}`;
};

const proposalTotal = (proposal) => proposal.price_model === "hourly"
  ? proposal.estimated_price_max ?? Math.round((proposal.hourly_rate || 0) * (proposal.estimated_minutes_max || 0) / 60)
  : proposal.fixed_price;

export default function ExpertProblemDetail() {
  const { pid } = useParams();
  const [p, setP] = useState(null);
  const [cat, setCat] = useState(null);
  const [profile, setProfile] = useState(null);
  const [showProposal, setShowProposal] = useState(false);
  const [alreadyProposed, setAlreadyProposed] = useState(false);
  const [acceptedProposal, setAcceptedProposal] = useState(null);
  const [confirmDecline, setConfirmDecline] = useState(false);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");
  const nav = useNavigate();
  const location = useLocation();
  const clarificationSectionRef = useRef(null);
  const handledChatTargetRef = useRef("");

  const reloadProposals = () => {
    api.get(`/problems/${pid}/proposals`).then((response) => {
      const proposals = response.data || [];
      setAlreadyProposed(proposals.some((proposal) => ["pending", "accepted"].includes(proposal.status)));
      setAcceptedProposal(proposals.find((proposal) => proposal.status === "accepted") || null);
    }).catch(() => {});
  };

  useEffect(() => {
    api.get(`/problems/${pid}`).then(async (response) => {
      setP(response.data);
      if (response.data.category_id) {
        const categories = await api.get("/categories").then((result) => result.data);
        setCat(categories.find((category) => category.id === response.data.category_id));
      }
    }).catch((requestError) => {
      setErr(requestError.response?.data?.detail || "Forespørgslen kunne ikke hentes.");
    });
    api.get("/auth/me").then((response) => setProfile(response.data?.expert_profile)).catch(() => {});
    reloadProposals();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pid]);

  useEffect(() => {
    if (!p || !["#chat", "#clarification"].includes(location.hash)) return;
    if (handledChatTargetRef.current === p.id) return;
    handledChatTargetRef.current = p.id;
    window.requestAnimationFrame(() => {
      clarificationSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
      clarificationSectionRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
    });
  }, [location.hash, p]);

  const canRespond = p && ["open", "clarification", "offer_received"].includes(p.status);
  const selected = p?.expert_state === "selected";
  const approved = profile?.approved === true && profile?.approval_status === "approved";

  const acceptForProposal = async () => {
    setBusy("accept");
    setErr("");
    try {
      await api.post(`/problems/${pid}/interest`);
      setP((current) => ({ ...current, expert_state: "interested" }));
      setShowProposal(true);
    } catch (requestError) {
      setErr(requestError.response?.data?.detail || "Opgaven kunne ikke accepteres.");
    } finally {
      setBusy("");
    }
  };

  const decline = async () => {
    setBusy("decline");
    setErr("");
    try {
      await api.post(`/problems/${pid}/decline`);
      nav("/expert?tab=requests", { replace: true });
    } catch (requestError) {
      setErr(requestError.response?.data?.detail || "Forespørgslen kunne ikke afvises.");
      setConfirmDecline(false);
    } finally {
      setBusy("");
    }
  };

  if (!p && !err) return <PageSkeleton cards={3}/>;
  if (!p) return (
    <div className="max-w-4xl mx-auto px-4 py-10">
      <div className="card-outline p-5 text-sm text-[#991B1B]">{err}</div>
      <Link to="/expert?tab=requests" className="mt-4 inline-flex text-sm text-brand-green">Tilbage til forespørgsler</Link>
    </div>
  );

  const duration = p.duration_min || 15;

  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-7">
      <Link to="/expert?tab=requests" className="text-sm text-brand-muted flex items-center gap-1 hover:text-brand-ink" data-testid="back-to-requests">
        <ArrowLeft size={14}/> Tilbage til forespørgsler
      </Link>

      <section className="mt-4 card-outline overflow-hidden" data-testid="request-summary">
        <header className="p-5 sm:p-6 border-b border-brand-line">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-brand-ink" data-testid="request-title">{p.title}</h1>
              <div className="mt-2 flex items-center gap-2 flex-wrap text-xs text-brand-muted">
                {cat && <span>{cat.name}</span>}
                {cat && <span>·</span>}
                <span>{p.help_timing === "now" ? "Haster" : "Kan planlægges"}</span>
                <span>·</span>
                <span className="inline-flex items-center gap-1"><Clock size={12}/> {duration} min.</span>
                <span>·</span>
                <span>{new Date(p.created_at).toLocaleString("da-DK")}</span>
              </div>
            </div>
            <div className="flex gap-1.5 flex-wrap">
              {selected && <span className="chip">Aktiv opgave</span>}
              {p.expert_state === "clarifying" && <span className="chip">Afklarer</span>}
              {p.expert_state === "interested" && <span className="chip">Klar til prisforslag</span>}
              {alreadyProposed && !selected && <span className="chip">Prisforslag sendt</span>}
            </div>
          </div>
        </header>

        <div className="p-5 sm:p-6">
          {p.customer_profile && (
            <div className="flex items-center gap-3 pb-5 border-b border-brand-line" data-testid="request-customer-profile">
              <ProfileAvatar photoUrl={p.customer_profile.photo_url} name={p.customer_profile.name} size="md"/>
              <div>
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">Kunde</div>
                <div className="font-medium text-brand-ink">{p.customer_profile.name}</div>
                {(p.customer_profile.city || p.customer_profile.language) && (
                  <div className="text-xs text-brand-muted">
                    {[p.customer_profile.city, p.customer_profile.language].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="py-5" data-testid="request-description">
            <div className="text-[10px] uppercase tracking-widest text-brand-muted">Problemet</div>
            <p className="mt-1.5 text-brand-body leading-relaxed whitespace-pre-wrap">{p.description}</p>
            {p.what_tried && (
              <div className="mt-4">
                <div className="text-[10px] uppercase tracking-widest text-brand-muted">Det har kunden allerede prøvet</div>
                <p className="mt-1 text-sm text-brand-body whitespace-pre-wrap">{p.what_tried}</p>
              </div>
            )}
            {p.specialties?.length > 0 && (
              <div className="mt-4 flex flex-wrap gap-1.5">
                {p.specialties.map((specialty) => <span key={specialty} className="chip">{specialty}</span>)}
              </div>
            )}
          </div>

          {p.media?.length > 0 && (
            <div className="border-t border-brand-line pt-5 pb-1" data-testid="request-attachments">
              <div className="inline-flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-brand-muted">
                <Paperclip size={12}/> Billeder og video
              </div>
              <div className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
                {p.media.map((media, index) => (
                  <PrivateAttachment
                    key={media.id || index}
                    attachment={{
                      ...media,
                      url: media.url?.startsWith("/api/") ? media.url.slice(4) : (media.url || media.dataUrl),
                      content_type: media.content_type || media.type,
                      name: media.name || `Vedhæftning ${index + 1}`,
                    }}
                  />
                ))}
              </div>
            </div>
          )}

          {selected && acceptedProposal && (
            <div className="mt-5 rounded-xl border border-brand-line-strong bg-brand-soft p-4 sm:p-5" data-testid="expert-active-agreement">
              <div className="font-display text-xl font-semibold text-[#166534]">Kunden har valgt dig</div>
              <div className="mt-1 text-xs text-brand-body">{p.payment_status === "paid" ? "Betalingen er bekræftet. Samtalen åbner på det aftalte tidspunkt." : p.payment_method === "manual_expert" ? "Aftalen er aktiv. Send betalingsoplysningerne nedenfor og bekræft først, når du har modtaget pengene." : "Aftalen er aktiv. Kunden skal gennemføre betalingen før I aftaler tidspunkt."}</div>
              <VideoConsultationEntry
                problem={p}
                role="expert"
                onOpenChat={() => {
                  clarificationSectionRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
                  clarificationSectionRef.current?.querySelector("textarea")?.focus({ preventScroll: true });
                }}
                onProblemUpdated={(videoState) => setP((current) => ({
                  ...current,
                  status: videoState.problem_status,
                  completion_status: videoState.completion_status,
                  completed_at: videoState.completed_at,
                  funds_state: videoState.funds_state,
                  funds_release_at: videoState.funds_release_at,
                  payment_status: videoState.payment_status ?? current.payment_status,
                  direct_payment_confirmed_at: videoState.direct_payment_confirmed_at ?? current.direct_payment_confirmed_at,
                }))}
              />
              {p.status === "offer_accepted" && <AgreementScheduling
                problem={p}
                className="mt-4"
                onUpdated={(schedule) => setP((current) => ({ ...current, ...schedule }))}
              />}
              <div className="mt-4 grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Pris</div>
                  <div className="font-medium text-brand-ink">{acceptedProposal.service_title || "Løsning af opgave"}</div>
                  <div className="mt-0.5 text-xs text-brand-body">Forventet {durationLabel(acceptedProposal.estimated_minutes_max || acceptedProposal.estimated_minutes_min)}</div>
                  {acceptedProposal.price_model === "hourly" && <div className="text-xs text-brand-body">{durationLabel(acceptedProposal.estimated_minutes_max)} × {formatDkk(acceptedProposal.hourly_rate)}/time</div>}
                  <div className="mt-1 font-semibold text-brand-green">Samlet pris: {formatDkk(proposalTotal(acceptedProposal))}</div>
                </div>
                <div>
                  <div className="text-[10px] uppercase tracking-widest text-brand-muted">Omfang</div>
                  <p className="text-sm text-brand-ink whitespace-pre-wrap">{acceptedProposal.scope}</p>
                </div>
              </div>
            </div>
          )}

          {!canRespond && !selected && (
            <div className="mt-5 rounded-lg bg-[#F7E4E4] p-3 text-sm text-[#991B1B]">
              Denne forespørgsel er ikke længere åben for nye svar.
            </div>
          )}

          {err && <div className="mt-4 text-sm text-[#991B1B]" data-testid="request-action-error">{err}</div>}

          {canRespond && approved && !alreadyProposed && (
            <div className="mt-5 border-t border-brand-line pt-5" data-testid="expert-request-actions">
              <div className="font-medium text-brand-ink">Vil du sende et prisforslag?</div>
              <div className="mt-3 flex gap-2 flex-wrap">
                <button onClick={acceptForProposal} disabled={!!busy} className="btn-primary disabled:opacity-50" data-testid="accept-request">
                  {busy === "accept" ? "Åbner…" : "Accepter opgave"}
                </button>
                <button onClick={() => setConfirmDecline(true)} disabled={!!busy} className="px-4 py-2 rounded-full border border-[#E7BDBD] text-sm text-[#991B1B] hover:bg-[#F7E4E4] disabled:opacity-50" data-testid="open-decline-confirmation">
                  Afvis
                </button>
              </div>
            </div>
          )}

          {alreadyProposed && !selected && (
            <div className="mt-5 rounded-lg bg-brand-soft p-3 text-sm text-[#166534]" data-testid="proposal-submitted-state">
              Dit prisforslag er sendt. Kunden beslutter stadig hvilken ekspert der vælges.
            </div>
          )}
        </div>
      </section>

      <CancellationAndSupport problem={p} role="expert" onUpdated={(updates) => setP((current) => ({ ...current, ...updates }))}/>

      <section id="chat" ref={clarificationSectionRef} className="mt-6 scroll-mt-24" data-testid="clarification-section">
        <div className="mb-2">
          <h2 className="font-display text-lg font-semibold text-brand-ink">Afklaring</h2>
          <p className="text-xs text-brand-muted">Skriv direkte til kunden hvis der mangler oplysninger.</p>
        </div>
        <ProblemClarification problemId={pid} role="expert"/>
      </section>

      {confirmDecline && (
        <div className="mt-4 rounded-lg border border-[#E7BDBD] bg-[#FFF9F7] p-4" role="dialog" aria-modal="true" data-testid="decline-confirmation">
          <div className="font-medium text-brand-ink">Vil du afvise denne forespørgsel?</div>
          <p className="mt-1 text-sm text-brand-muted">Den fjernes kun fra dine aktive forespørgsler.</p>
          <div className="mt-3 flex gap-2">
            <button onClick={() => setConfirmDecline(false)} disabled={!!busy} className="btn-secondary">Behold forespørgsel</button>
            <button onClick={decline} disabled={!!busy} className="px-4 py-2 rounded-full bg-[#991B1B] text-white disabled:opacity-50" data-testid="confirm-decline">
              {busy === "decline" ? "Afviser…" : "Afvis"}
            </button>
          </div>
        </div>
      )}

      {showProposal && (
        <ProposalForm
          request={p}
          minPrice={profile?.price_min || 0}
          hourlyRate={profile?.hourly_rate || 0}
          onClose={() => setShowProposal(false)}
          onSubmitted={() => {
            setShowProposal(false);
            setAlreadyProposed(true);
            setP((current) => ({ ...current, expert_state: "proposal_submitted" }));
            reloadProposals();
          }}
        />
      )}
    </div>
  );
}
